import { useState } from 'react';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    organization: '',
    facilityType: '',
    modality: '',
    location: '',
    timeframe: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d63scklanburc340ql2g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          organization: '',
          facilityType: '',
          modality: '',
          location: '',
          timeframe: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      <main className="pt-24 sm:pt-32 pb-12 sm:pb-20">
        {/* Hero Section */}
        <section className="px-4 sm:px-6 mb-12 sm:mb-20">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-slate-blue text-[13px] uppercase tracking-[2px] font-medium mb-3 sm:mb-4">
              CONTACT US
            </p>
            <h1 className="text-navy text-[28px] sm:text-[40px] lg:text-[48px] font-semibold leading-tight mb-4 sm:mb-6">
              Request a Quote or Consultation
            </h1>
            <p className="text-[#5A6C7D] text-base sm:text-lg leading-relaxed">
              Our team is ready to discuss your mobile imaging needs and provide a customized solution for your facility. We typically respond within 24 hours.
            </p>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-16">
            {/* Contact Information */}
            <div className="lg:col-span-1 order-2 lg:order-1">
              <h2 className="text-navy text-xl sm:text-2xl font-semibold mb-6 sm:mb-8">Get in Touch</h2>
              
              <div className="grid grid-cols-1 gap-5 sm:gap-6">
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-slate-blue rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-white text-lg sm:text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-navy font-semibold mb-1 text-sm sm:text-base">Email</h3>
                    <p className="text-[#5A6C7D] text-sm sm:text-[15px]">admin@medilinemedical.com</p>
                    <p className="text-[#5A6C7D] text-xs sm:text-sm">24-hour response time</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-slate-blue rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-white text-lg sm:text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-navy font-semibold mb-1 text-sm sm:text-base">Headquarters</h3>
                    <p className="text-[#5A6C7D] text-sm sm:text-[15px]">
                      Sacramento, California<br />
                      Serving all of California
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-slate-blue rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-white text-lg sm:text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-navy font-semibold mb-1 text-sm sm:text-base">Emergency Support</h3>
                    <p className="text-[#5A6C7D] text-sm sm:text-[15px]">24/7 availability</p>
                    <p className="text-[#5A6C7D] text-xs sm:text-sm">For urgent deployments</p>
                  </div>
                </div>
              </div>

              <div className="mt-8 sm:mt-12 p-5 sm:p-6 bg-[#F7F9FB] rounded-xl">
                <h3 className="text-navy font-semibold mb-2 sm:mb-3 text-sm sm:text-base">Quick Response Guarantee</h3>
                <p className="text-[#5A6C7D] text-xs sm:text-sm leading-relaxed">
                  We understand that healthcare facilities operate on tight timelines. Our team commits to responding to all inquiries within 24 hours, with emergency requests receiving immediate attention.
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2 order-1 lg:order-2">
              <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl p-5 sm:p-8 lg:p-10">
                <h2 className="text-navy text-xl sm:text-2xl font-semibold mb-6 sm:mb-8">Request Information</h2>
                
                <form onSubmit={handleSubmit} data-readdy-form id="contact-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
                    <div>
                      <label htmlFor="name" className="block text-navy font-medium mb-1.5 sm:mb-2 text-sm">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-blue text-sm"
                        placeholder="John Smith"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-navy font-medium mb-1.5 sm:mb-2 text-sm">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-blue text-sm"
                        placeholder="john.smith@hospital.com"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
                    <div>
                      <label htmlFor="phone" className="block text-navy font-medium mb-1.5 sm:mb-2 text-sm">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-blue text-sm"
                        placeholder="(555) 123-4567"
                      />
                    </div>

                    <div>
                      <label htmlFor="organization" className="block text-navy font-medium mb-1.5 sm:mb-2 text-sm">
                        Organization Name *
                      </label>
                      <input
                        type="text"
                        id="organization"
                        name="organization"
                        value={formData.organization}
                        onChange={handleChange}
                        required
                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-blue text-sm"
                        placeholder="Hospital or Facility Name"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
                    <div>
                      <label htmlFor="facilityType" className="block text-navy font-medium mb-1.5 sm:mb-2 text-sm">
                        Facility Type *
                      </label>
                      <select
                        id="facilityType"
                        name="facilityType"
                        value={formData.facilityType}
                        onChange={handleChange}
                        required
                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-blue text-sm cursor-pointer"
                      >
                        <option value="">Select facility type</option>
                        <option value="hospital">Hospital / Health System</option>
                        <option value="physician">Physician Group</option>
                        <option value="imaging">Imaging Center</option>
                        <option value="surgical">Surgical Center</option>
                        <option value="rural">Rural / Critical Access</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="modality" className="block text-navy font-medium mb-1.5 sm:mb-2 text-sm">
                        Imaging Modality Needed *
                      </label>
                      <select
                        id="modality"
                        name="modality"
                        value={formData.modality}
                        onChange={handleChange}
                        required
                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-blue text-sm cursor-pointer"
                      >
                        <option value="">Select modality</option>
                        <option value="mri">Mobile MRI</option>
                        <option value="ct">Mobile CT</option>
                        <option value="ultrasound">Mobile Ultrasound</option>
                        <option value="xray">Mobile X-Ray</option>
                        <option value="multiple">Multiple Modalities</option>
                        <option value="unsure">Not Sure Yet</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
                    <div>
                      <label htmlFor="location" className="block text-navy font-medium mb-1.5 sm:mb-2 text-sm">
                        Facility Location (City) *
                      </label>
                      <input
                        type="text"
                        id="location"
                        name="location"
                        value={formData.location}
                        onChange={handleChange}
                        required
                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-blue text-sm"
                        placeholder="Los Angeles, CA"
                      />
                    </div>

                    <div>
                      <label htmlFor="timeframe" className="block text-navy font-medium mb-1.5 sm:mb-2 text-sm">
                        Deployment Timeframe *
                      </label>
                      <select
                        id="timeframe"
                        name="timeframe"
                        value={formData.timeframe}
                        onChange={handleChange}
                        required
                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-blue text-sm cursor-pointer"
                      >
                        <option value="">Select timeframe</option>
                        <option value="emergency">Emergency (48-72 hours)</option>
                        <option value="1-2weeks">1-2 Weeks</option>
                        <option value="1month">Within 1 Month</option>
                        <option value="1-3months">1-3 Months</option>
                        <option value="3+months">3+ Months</option>
                        <option value="planning">Planning / Exploring Options</option>
                      </select>
                    </div>
                  </div>

                  <div className="mb-4 sm:mb-6">
                    <label htmlFor="message" className="block text-navy font-medium mb-1.5 sm:mb-2 text-sm">
                      Additional Details
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={4}
                      maxLength={500}
                      className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-blue text-sm resize-none"
                      placeholder="Please provide any additional information about your needs..."
                    ></textarea>
                    <p className="text-[#5A6C7D] text-xs mt-1.5 sm:mt-2">Maximum 500 characters</p>
                  </div>

                  {submitStatus === 'success' && (
                    <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-green-800 text-sm font-medium">
                        Thank you! Your request has been submitted successfully. We&apos;ll contact you within 24 hours.
                      </p>
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-red-800 text-sm font-medium">
                        There was an error submitting your request. Please try again or contact us directly.
                      </p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-navy text-white py-3.5 sm:py-4 rounded-lg font-semibold text-base hover:bg-navy-light transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Request'}
                  </button>

                  <p className="text-[#5A6C7D] text-xs text-center mt-3 sm:mt-4">
                    By submitting this form, you agree to be contacted by Mediline Services regarding your inquiry.
                  </p>
                </form>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
